// Offline Storage Manager for PWA functionality

const DB_NAME = 'magicstay_offline';
const DB_VERSION = 1;
const STORES = {
  PROPERTIES: 'saved_properties',
  FAVORITES: 'favorites',
  SEARCHES: 'searches'
};

class OfflineStorageManager {
  constructor() {
    this.db = null;
    this.isSupported = 'indexedDB' in window;
  }

  async init() {
    if (!this.isSupported) {
      console.warn('IndexedDB not supported');
      return false;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(true);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create stores if they don't exist
        if (!db.objectStoreNames.contains(STORES.PROPERTIES)) {
          db.createObjectStore(STORES.PROPERTIES, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.FAVORITES)) {
          db.createObjectStore(STORES.FAVORITES, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.SEARCHES)) {
          db.createObjectStore(STORES.SEARCHES, { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  async saveProperty(property) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PROPERTIES], 'readwrite');
      const store = transaction.objectStore(STORES.PROPERTIES);
      
      const propertyWithTimestamp = {
        ...property,
        savedAt: new Date().toISOString(),
        offlineAvailable: true
      };

      const request = store.put(propertyWithTimestamp);

      request.onsuccess = () => resolve(propertyWithTimestamp);
      request.onerror = () => reject(request.error);
    });
  }

  async getProperty(id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PROPERTIES], 'readonly');
      const store = transaction.objectStore(STORES.PROPERTIES);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllProperties() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PROPERTIES], 'readonly');
      const store = transaction.objectStore(STORES.PROPERTIES);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async removeProperty(id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PROPERTIES], 'readwrite');
      const store = transaction.objectStore(STORES.PROPERTIES);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async saveFavorite(property) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FAVORITES], 'readwrite');
      const store = transaction.objectStore(STORES.FAVORITES);
      
      const favoriteWithTimestamp = {
        ...property,
        addedAt: new Date().toISOString()
      };

      const request = store.put(favoriteWithTimestamp);

      request.onsuccess = () => resolve(favoriteWithTimestamp);
      request.onerror = () => reject(request.error);
    });
  }

  async getFavorites() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FAVORITES], 'readonly');
      const store = transaction.objectStore(STORES.FAVORITES);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async removeFavorite(id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FAVORITES], 'readwrite');
      const store = transaction.objectStore(STORES.FAVORITES);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async saveSearch(searchParams) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SEARCHES], 'readwrite');
      const store = transaction.objectStore(STORES.SEARCHES);
      
      const searchWithTimestamp = {
        ...searchParams,
        savedAt: new Date().toISOString()
      };

      const request = store.add(searchWithTimestamp);

      request.onsuccess = () => resolve(searchWithTimestamp);
      request.onerror = () => reject(request.error);
    });
  }

  async getRecentSearches(limit = 10) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SEARCHES], 'readonly');
      const store = transaction.objectStore(STORES.SEARCHES);
      const request = store.getAll();

      request.onsuccess = () => {
        const searches = request.result || [];
        // Sort by date and limit
        const sorted = searches
          .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt))
          .slice(0, limit);
        resolve(sorted);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearAll() {
    if (!this.db) await this.init();
    
    const promises = Object.values(STORES).map(storeName => {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    });

    return Promise.all(promises);
  }

  // Check if browser is online
  isOnline() {
    return navigator.onLine;
  }

  // Listen to online/offline events
  onConnectionChange(callback) {
    window.addEventListener('online', () => callback(true));
    window.addEventListener('offline', () => callback(false));
    
    // Return unsubscribe function
    return () => {
      window.removeEventListener('online', () => callback(true));
      window.removeEventListener('offline', () => callback(false));
    };
  }
}

export const offlineStorage = new OfflineStorageManager();
export default offlineStorage;
